import { useCallback, useEffect, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Tag from '../ui/Tag';
import Table from '../ui/Table';
import { useToast } from '../ui/Toast';
import { formatAmount } from '../ui/Chart';
import { clean } from '../shell/copy';
import { METRICS } from '../pages/reports/outputModel';

// Admin → Targets (OUT-2, OUT-4; docs/OUTPUT-C5-v1.md §5 and §7.4, DESIGN rev 2.3). One row per person per metric they
// hold a role for (Scripted: Writer, Filmed: Producer, Edited: Editor; GET /api/output/targets byMetric, zeros included,
// ruling H): the daily target in minutes as an inline field; this week's working days, target and amount so far from
// GET /api/output/summary (range=week: the whole week's working days and target, OUT-2.1). Save writes the changed rows as
// one array to PUT /api/output/targets, effective tomorrow (the API keeps history). The working-day window for pace is two
// fields saved to PUT /api/output/settings with the same Save.
// The tab is shown to Admin, Manager and Power User only (pages/Admin.jsx), the roles the API lets write targets.
const errOf = (e, fallback) => clean(e?.response?.data?.error) || fallback;
const dayName = ymd => new Date(`${ymd}T00:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' });
const asNumber = v => (v === '' || v == null ? null : Number(v));

export default function OutputTargets() {
  const toast = useToast();
  const [state, setState] = useState('loading');
  const [data, setData] = useState(null);       // { targets, week, settings }
  const [edits, setEdits] = useState({});       // { 'id|metric': string }
  const [win, setWin] = useState(null);         // { start, end } as edited
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setState(s => (s === 'data' ? s : 'loading'));
    try {
      // The pace window is optional: an API without /settings (404) leaves the targets working and says so below.
      const [t, w, st] = await Promise.all([api.get('/api/output/targets'), api.get('/api/output/summary?range=week'),
        api.get('/api/output/settings').then(r => r.data, () => null)]);
      setData({ targets: t.data, week: w.data, settings: st });
      setEdits({});
      setWin(st ? st.paceWindow : null);
      setState('data');
    } catch { setState('error'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    return METRICS.flatMap(([metric, label]) => {
      const week = new Map((data.week.metrics[metric]?.people || []).filter(p => p.teamMemberId).map(p => [p.teamMemberId, p]));
      return (data.targets.byMetric?.[metric] || []).map(p => ({ key: `${p.teamMemberId}|${metric}`, id: p.teamMemberId, name: p.name, metric, label,
        current: p.target, next: p.next, week: week.get(p.teamMemberId) || null }));
    });
  }, [data]);

  const valueOf = r => (edits[r.key] !== undefined ? edits[r.key] : (r.next ?? r.current ?? '') === '' ? '' : String(r.next ?? r.current));
  const invalid = r => { const v = edits[r.key]; return v !== undefined && v !== '' && !(Number(v) >= 0 && Number.isFinite(Number(v))); };
  const changed = rows.filter(r => edits[r.key] !== undefined && edits[r.key] !== '' && !invalid(r) && asNumber(edits[r.key]) !== (r.next ?? r.current));
  const winChanged = data && data.settings && win && (win.start !== data.settings.paceWindow.start || win.end !== data.settings.paceWindow.end);
  const winError = win && win.start && win.end && win.start >= win.end ? 'The day must start before it ends.' : (win && (!win.start || !win.end) ? 'Enter both times.' : null);
  const anyInvalid = rows.some(invalid) || !!winError;
  const dirty = changed.length > 0 || winChanged;

  async function save() {
    setSaving(true);
    try {
      if (changed.length) await api.put('/api/output/targets', changed.map(r => ({ teamMemberId: r.id, metric: r.metric, dailyTarget: Number(edits[r.key]) })));
      if (winChanged) await api.put('/api/output/settings', { start: win.start, end: win.end });
      const parts = [changed.length ? `${changed.length === 1 ? '1 target' : `${changed.length} targets`} saved; they apply from ${dayName(data.targets.effectiveFromNext)}` : null,
        winChanged ? 'working day saved' : null].filter(Boolean);
      toast(`${parts.join(', ').replace(/^./, c => c.toUpperCase())}.`);
      await load();
    } catch (e) { toast(errOf(e, 'The targets did not save. Try again.'), { tone: 'danger' }); }
    finally { setSaving(false); }
  }

  const columns = [
    { key: 'name', label: 'Person', width: '22%', render: r => clean(r.name) },
    { key: 'metric', label: 'Metric', width: 96, keep: true, render: r => <Tag>{r.label}</Tag> },   // kept on a phone: a person can hold three rows
    { key: 'target', label: 'Daily target', width: 160, keep: true, render: r => (
      <span className="ui-output__target">
        <Input type="number" inputMode="decimal" min="0" step={0.5} className="ui-output__field" aria-label={`Daily ${r.label.toLowerCase()} target for ${clean(r.name)}, minutes`}
          placeholder="Not set" value={valueOf(r)} onChange={e => setEdits(x => ({ ...x, [r.key]: e.target.value }))}
          aria-invalid={invalid(r) || undefined} error={invalid(r) ? 'A number, 0 or more' : undefined}
          hint={r.next != null && r.next !== r.current ? `${r.current != null ? `${formatAmount(r.current, 'min')} today, ` : ''}from tomorrow` : undefined} />
        <span className="ui-text-3">min</span>
      </span>
    ) },
    { key: 'days', label: 'Working days', align: 'right', render: r => (r.week
      ? <span className="ui-num">{formatAmount(r.week.workingDays, 'min').replace(/\.0$/, '')}{r.week.offDays > 0 && <span className="ui-text-3"> ({formatAmount(r.week.offDays, 'min').replace(/\.0$/, '')} off)</span>}</span>
      : <span className="ui-text-3">Not counted yet</span>) },
    { key: 'week', label: 'Target', align: 'right', render: r => (r.week && r.week.target != null
      ? <span className="ui-num">{formatAmount(r.week.target, 'min')} min</span>
      : <span className="ui-text-3">No target</span>) },
    { key: 'sofar', label: 'Done so far', align: 'right', render: r => <span className="ui-num">{formatAmount(r.week ? r.week.amount : 0, 'min')} min</span> },
  ];

  return (
    <section className="ui-section" aria-labelledby="targets-h">
      <div className="ui-section__head ui-section__head--center">
        <h2 className="ui-section__title" id="targets-h">Daily targets</h2>
        <div className="ui-section__actions">
          <Button variant="primary" icon={Save} onClick={save} loading={saving} disabled={!dirty || anyInvalid || state !== 'data'}>Save targets</Button>
        </div>
      </div>
      <p className="ui-section__note">
        Minutes of finished video a day, one number per person for each metric they hold a role for: Scripted for writers,
        Filmed for producers, Edited for editors. The week is the sum of their working days; days off in Time off are taken
        out automatically.
        <strong className="ui-output__strong"> Changes apply from tomorrow.</strong>
        {data && <span className="ui-text-3"> This week: {dayName(data.week.range.from)} to {dayName(data.week.range.to)}, days off ahead included.</span>}
      </p>
      <div className="ui-region">
        <Table caption="Daily targets" columns={columns} rows={rows} rowKey={r => r.key} state={state === 'error' ? 'error' : state === 'loading' ? 'loading' : 'data'}
          error={{ title: 'Targets did not load', message: 'Check your connection, then try again.', onRetry: load }}
          empty={{ message: 'No active writers, producers or editors yet. Give team members the Writer, Producer or Editor role in Team members.' }} />
      </div>

      {state === 'data' && !data.settings && (
        <p className="ui-section__note">The working day for pace could not be read from this server, so it keeps its default, 09:00 to 17:00 New York. Targets save as usual.</p>
      )}
      {state === 'data' && win && (
        <div className="ui-stack">
          <h3 className="ui-heading">Working day for pace</h3>
          <p className="ui-section__note">The pace mark on Reports, Output moves across this window each working day. New York time.</p>
          <div className="ui-cluster ui-output__window">
            <Input type="time" label="Day starts" value={win.start} onChange={e => setWin(w => ({ ...w, start: e.target.value }))} error={winError || undefined} />
            <Input type="time" label="Day ends" value={win.end} onChange={e => setWin(w => ({ ...w, end: e.target.value }))} />
          </div>
        </div>
      )}
    </section>
  );
}
